import { Button, Group, useComputedColorScheme } from "@mantine/core";
import { IconFocusCentered } from "@tabler/icons-react";
import * as echarts from "echarts/core";
import { useId, useMemo, useState } from "react";

import { EChart, type EChartOption } from "@/viz/EChart";
import { VIZ_FONT, mantineColor, vizTokens } from "@/viz/tokens";

export interface ClusterStatus {
  value: string;
  label: string;
  color: string;
}

/**
 * The map and its table twin must never disagree on the same data, so this
 * label rule lives in exactly one place rather than being reimplemented at
 * each call site.
 */
export function statusLabel(status: string | null, styles: ClusterStatus[]): string {
  if (status == null) return "—";
  return styles.find((s) => s.value === status)?.label ?? "Unrecognised";
}

interface Feature {
  properties: { name: string; role: "cluster" | "reference" };
  geometry: { type: string; coordinates: unknown };
}

export interface GeoJsonCollection {
  type: "FeatureCollection";
  features: Feature[];
}

interface Props {
  geojson: GeoJsonCollection;
  keys: string[];
  values: (number | null)[];
  /** One status per key, aligned to it. Null is "no status", not an error. */
  statuses: (string | null)[];
  statusStyles: ClusterStatus[];
  height: number;
  valueLabel: string;
  /** Label each cluster with its key. */
  labels?: boolean;
}

/** Opaque enough to read, transparent enough to show the reference layer beneath. */
const FILL_OPACITY = 0.65;

/**
 * The bounding box of the *cluster* features only.
 *
 * Excluding the reference layer is the whole point: a country-wide admin
 * boundary would otherwise zoom the opening view out to the whole country,
 * when what someone wants to see is the study area.
 */
export function boundingCoords(
  geojson: GeoJsonCollection,
): [[number, number], [number, number]] | undefined {
  let minLon = Infinity;
  let minLat = Infinity;
  let maxLon = -Infinity;
  let maxLat = -Infinity;

  const visit = (node: unknown): void => {
    if (!Array.isArray(node)) return;
    if (typeof node[0] === "number" && typeof node[1] === "number") {
      const [lon, lat] = node as [number, number];
      minLon = Math.min(minLon, lon);
      maxLon = Math.max(maxLon, lon);
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
      return;
    }
    node.forEach(visit);
  };

  geojson.features
    .filter((f) => f.properties.role === "cluster")
    .forEach((f) => visit(f.geometry.coordinates));

  if (!Number.isFinite(minLon)) return undefined;
  // ECharts takes top-left then bottom-right.
  return [
    [minLon, maxLat],
    [maxLon, minLat],
  ];
}

/**
 * Choropleth of cluster status over the study area, with a reference boundary
 * (e.g. the enclosing admin region) drawn underneath for orientation.
 *
 * Both roles are rendered from ONE registered map, not two series: an ECharts
 * `map` series owns its own coordinate system, so a boundary series and a
 * cluster series would not reliably pan and zoom together. The backend's
 * geometry endpoint composes both roles into a single FeatureCollection for
 * exactly this reason. `nameProperty: "name"` then binds each `data` entry to
 * its feature by name rather than array order, so `data` below is built in
 * whatever order is convenient as long as every `name` matches
 * `properties.name` exactly — including the `ref:` prefix the backend already
 * put on reference feature names.
 */
export function ClusterMap({
  geojson,
  keys,
  values,
  statuses,
  statusStyles,
  height,
  valueLabel,
  labels = false,
}: Props) {
  const scheme = useComputedColorScheme("light", { getInitialValueInEffect: true });

  // echarts.registerMap's storage is one registry shared by every ECharts
  // instance on the page, not scoped per component — so the name must be
  // unique per mounted map, or two map widgets on the same dashboard can
  // collide and one will render the other's shapes. useId is stable for the
  // life of this component instance and unique across instances, which is
  // exactly that guarantee; it does not need to change when `geojson` does.
  // Re-registering the same name with new geometry on a later render is
  // already safe: registerMap overwrites its stored resource unconditionally,
  // and the map series rebuilds its coordinate system from the registry on
  // every setOption, so a fresh payload under the same name draws correctly.
  const mapName = `cluster-map-${useId()}`;

  // Bumped by the reset control. It does nothing but make `option` a new
  // object, which is the whole mechanism: EChart applies every option with
  // `notMerge: true`, and roam writes the user's pan and zoom back into the
  // series model, so a rebuilt option is a view returned to `boundingCoords`.
  // If that ever becomes a merge, this control needs its own implementation.
  const [resetCount, setResetCount] = useState(0);

  // Paint order is feature order — ECharts adds one region group per feature
  // and ZRender paints a group's children in insertion order — and the
  // reference layer is an opaque backdrop, so a reference feature after a
  // cluster hides it. The backend already composes reference-first; re-sorting
  // here costs one stable pass and means neither side can silently blank the
  // map on its own. ECharts offers nothing better: region z-order is not
  // settable per data item, only implied by this order.
  const ordered = useMemo<GeoJsonCollection>(
    () => ({
      ...geojson,
      features: [...geojson.features].sort(
        (a, b) =>
          Number(a.properties.role === "cluster") -
          Number(b.properties.role === "cluster"),
      ),
    }),
    [geojson],
  );

  const option = useMemo<EChartOption>(() => {
    const t = vizTokens(scheme);
    echarts.registerMap(mapName, ordered as never);

    const fillByStatus = new Map<string, string>();
    for (const style of statusStyles) {
      fillByStatus.set(style.value, mantineColor(style.color, scheme));
    }

    const indexByKey = new Map(keys.map((k, i) => [k, i]));

    const data = ordered.features.map((f) => {
      if (f.properties.role === "reference") {
        return {
          name: f.properties.name,
          role: "reference",
          itemStyle: {
            // `gridline`, not `surface`: the reference layer needs a fill one
            // visible step off the card background, or it disappears entirely
            // under a status-colored cluster. It still stays subordinate to
            // the cluster fills above it, which is the point of the layer.
            areaColor: t.gridline,
            borderColor: t.axis,
            borderWidth: 1,
          },
          // The reference layer carries no value, so a hover highlight would
          // invite a click that has nothing to show.
          emphasis: { disabled: true },
          // Series-level `label.show: false` keeps the 65 status clusters
          // free of overlapping text; the handful of reference regions is
          // the one place a label reads as orientation rather than clutter,
          // so it opts back in per data item. The formatter strips the
          // backend's `ref:` disambiguation prefix (added so a reference
          // region can't collide with a cluster's name in ECharts' shared
          // namespace) — that prefix is for `nameProperty` binding, not for
          // anyone to read.
          label: {
            show: true,
            formatter: () => f.properties.name.replace(/^ref:/, ""),
            color: t.textSecondary,
            fontFamily: VIZ_FONT,
            fontSize: 10,
          },
        };
      }

      const index = indexByKey.get(f.properties.name);
      const status = index === undefined ? null : statuses[index];
      // A status the data carries but that isn't declared in statusStyles (a
      // typo) gets the same neutral fill as no status at all — never a real
      // status color, so a typo can't quietly pass as a legitimate status.
      const fill = (status && fillByStatus.get(status)) || t.otherFill;

      return {
        name: f.properties.name,
        role: "cluster",
        value: index === undefined ? null : values[index],
        // Null is "no status" — a boundary can go undocumented; that's a
        // footnote, not an error. A non-null status absent from statusStyles
        // is the typo case. The two read differently in the tooltip on
        // purpose — see `statusLabel`'s own doc for why the rule lives there.
        statusLabel: statusLabel(status, statusStyles),
        itemStyle: {
          areaColor: fill,
          // Set as its own field rather than folded into an rgba() string:
          // ECharts' canvas/SVG renderer applies color and opacity as
          // separate paint attributes regardless, so composing an rgba()
          // string here would just be undone one layer down.
          opacity: FILL_OPACITY,
          borderColor: t.axis,
          borderWidth: 1,
        },
      };
    });

    return {
      backgroundColor: "transparent",
      tooltip: {
        trigger: "item",
        backgroundColor: t.tooltipBg,
        borderColor: t.tooltipBorder,
        borderWidth: 1,
        padding: [8, 12],
        textStyle: { color: t.textPrimary, fontFamily: VIZ_FONT, fontSize: 13 },
        extraCssText: "border-radius:8px; box-shadow:0 4px 16px rgba(0,0,0,0.12);",
        formatter: (params: never) => {
          const p = params as unknown as {
            data?: { role?: string; statusLabel?: string; value?: number | null };
            name: string;
          };
          // The reference layer gets no tooltip at all, not an empty one.
          if (!p.data || p.data.role !== "cluster") return "";
          const count = p.data.value == null ? "—" : p.data.value.toLocaleString();
          return (
            `<strong>${escapeHtml(p.name)}</strong><br/>` +
            `<span style="color:${t.textSecondary}">Status:</span> ` +
            `${escapeHtml(p.data.statusLabel ?? "")}<br/>` +
            `<span style="color:${t.textSecondary}">${escapeHtml(valueLabel)}:</span> ${count}`
          );
        },
      },
      series: [
        {
          type: "map",
          map: mapName,
          nameProperty: "name",
          roam: true,
          boundingCoords: boundingCoords(ordered),
          selectedMode: false,
          // Off unless the widget opts in. When on, hideOverlap is what keeps
          // hundreds of clusters legible: the opening view shows a scattering
          // of labels and the rest appear as the reader zooms in. Reference
          // regions set their own label per data item, so this only governs
          // the clusters.
          label: labels
            ? {
                show: true,
                color: t.textPrimary,
                fontFamily: VIZ_FONT,
                fontSize: 10,
                textBorderColor: t.surface,
                textBorderWidth: 2,
              }
            : { show: false },
          labelLayout: labels ? { hideOverlap: true } : undefined,
          data,
        },
      ],
    } as EChartOption;
  }, [
    ordered,
    keys,
    values,
    statuses,
    statusStyles,
    mapName,
    scheme,
    valueLabel,
    labels,
    resetCount,
  ]);

  return (
    <>
      {/* With roam on and nothing to undo it, panning off the study area
          leaves reloading the page as the only way back. */}
      <Group justify="flex-end" mb={4}>
        <Button
          size="xs"
          variant="subtle"
          leftSection={<IconFocusCentered size={14} />}
          onClick={() => setResetCount((n) => n + 1)}
        >
          Reset view
        </Button>
      </Group>
      <EChart
        option={option}
        height={height}
        ariaLabel={`Map of ${keys.length} clusters by status. The table view lists every cluster.`}
      />
    </>
  );
}

function escapeHtml(value: string): string {
  return value.replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!,
  );
}
